import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import Skeleton from './common/Skeleton';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

function TrendView({ repoName }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!repoName) return;
    setLoading(true);
    setError('');
    axios.get(`${config.BASE_URL}/sbom/${repoName}/trend`)
      .then(r => setData(r.data || []))
      .catch(err => {
        if (err.response?.status === 404) {
          setData([]);
        } else {
          setError(err.response?.data?.error || err.message);
        }
      })
      .finally(() => setLoading(false));
  }, [repoName]);

  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Card />
      <div className="small text-secondary mt-3">{t('trend.loading', 'Loading trend data...')}</div>
    </div>
  );

  if (error) return <div className="alert alert-danger">{error}</div>;

  if (!data || data.length === 0) return (
    <div className="ce-blank">
      <div className="ce-blank-icon">📈</div>
      <div className="ce-blank-title">{t('trend.noData')}</div>
      <div className="ce-blank-sub">{t('trend.noDataHint')}</div>
    </div>
  );

  const chartData = data.map(entry => ({
    label: entry.commitHash ? entry.commitHash.substring(0, 8) : entry.date,
    score: entry.score,
    passed: entry.passed,
    date: entry.date,
    totalPackages: entry.totalPackages,
    packagesWithPurl: entry.packagesWithPurl,
    packagesWithCpe: entry.packagesWithCpe,
    packagesMissingBothIds: entry.packagesMissingBothIds,
  }));

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    return (
      <circle
        cx={cx} cy={cy} r={5}
        fill={payload.passed ? '#198754' : '#dc3545'}
        stroke="#fff" strokeWidth={2}
      />
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div className="ce-card" style={{ padding: '0.625rem 0.875rem', fontSize: '0.8125rem', minWidth: 180 }}>
          <div><strong>{t('trend.commit', 'Commit')}:</strong> {label}</div>
          {d.date && <div><strong>{t('trend.date', 'Date')}:</strong> {d.date}</div>}
          <div><strong>{t('trend.score', 'Score')}:</strong> {d.score}</div>
          <div><strong>{t('trend.status', 'Status')}:</strong>{' '}
            <span style={{ color: d.passed ? '#16a34a' : '#dc2626' }}>
              {d.passed ? t('trend.pass', '✅ Pass') : t('trend.fail', '❌ Fail')}
            </span>
          </div>
          {d.totalPackages > 0 && (
            <>
              <div><strong>{t('trend.purl', 'PURL')}:</strong> {d.packagesWithPurl}/{d.totalPackages}</div>
              <div><strong>{t('trend.cpe', 'CPE')}:</strong> {d.packagesWithCpe}/{d.totalPackages}</div>
              <div><strong>{t('trend.identifierGaps', 'Identifier gaps')}:</strong> {d.packagesMissingBothIds}</div>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="ce-card">
        <div className="ce-card-header">
          <h6 className="ce-card-title">📈 {t('trend.title')}</h6>
        </div>
        <div className="ce-card-body">
          <p style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', marginBottom: '1rem' }}>
            {t('trend.identifierSubtitle', 'Score over time across commits. Green dots = pass, red dots = fail. Tooltips also show identifier readiness for each commit.')}
          </p>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine y={70} stroke="#aaa" strokeDasharray="4 2" label={{ value: t('trend.passThreshold', 'Pass threshold'), position: 'insideTopRight', fontSize: 11, fill: '#aaa' }} />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#0d6efd"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default TrendView;
